import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  Download,
  Link2,
  Sparkles,
  Coffee,
  Copy,
  Check,
  Loader2,
} from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { cuppingsPath, coffeesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ScoreRadarChart } from "@/components/charts/ScoreRadarChart"
import { getDescriptorLabel } from "@/components/DescriptorModal"

interface Sample {
  id: number
  name: string
  origin?: string
  land?: string
  producer?: string
  variety?: string
  process?: string
}

interface UserSample {
  id: number
  sample: Sample
  total_cups: number
  roast_level?: string
  fragrance_score: number
  acidity_score: number
  body_score: number
  flavor_score: number
  aftertaste_score: number
  balance_score: number
  overall_score: number
  uniformity_score: number
  clean_cup_score: number
  sweetness_score: number
  defects_cups: number
  defects_intensity: number
  notes: string
  total_score: number
  aroma_rating?: number
  non_uniform_cups?: number[]
  defective_cups_list?: number[]
  defect_type?: string
  user_name?: string
  aroma_sca_selections?: Record<string, string[]>
}

interface Organization {
  name: string
  logo_url?: string
}

interface Cupping {
  id: number
  title: string
  created_at: string
  cupping_form?: string
  participant_count?: number
  organization?: Organization
  user_samples: UserSample[]
}

interface Props {
  cupping: Cupping
  user_sample: UserSample
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Sesiones de catación",
    href: cuppingsPath(),
  },
]

const getScoreGrade = (score: number): string => {
  if (score >= 90) return "A+"
  if (score >= 85) return "A"
  if (score >= 80) return "B+"
  if (score >= 75) return "B"
  if (score >= 70) return "C"
  return "D"
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return "bg-green-600"
  if (score >= 85) return "bg-green-500"
  if (score >= 80) return "bg-blue-500"
  if (score >= 75) return "bg-primary"
  if (score >= 70) return "bg-yellow-500"
  return "bg-red-500"
}

const getRoastLevelColor = (level: string): string => {
  switch (level) {
    case "light":
      return "#D4A574"
    case "mid-light":
      return "#B8865F"
    case "medium":
      return "#8B5A3C"
    case "mid-dark":
      return "#6B4423"
    case "dark":
      return "#3E2723"
    default:
      return "#8B5A3C"
  }
}

const protocolLabels: Record<string, string> = {
  arabica: "Arábica",
  robusta: "Robusta",
  cup_of_excellence: "Taza de Excelencia",
  cva_affective: "CVA Afectivo",
  cva_descriptive: "CVA Descriptivo",
  cva_combined: "CVA Combinado",
  sca_cva_affective: "SCA CVA Afectivo",
  sca_cva_descriptive: "SCA CVA Descriptivo",
  sca_cva_combined: "SCA CVA Combinado",
}

const isCvaProtocol = (form?: string) => !!form && form.includes("cva")

interface TastingNotes {
  consumer: string
  technical: string
  flavor_tags: string[]
}

interface BrewRecipe {
  method: string
  dose_g: number
  water_ml: number
  temp_c: number
  grind: string
  time: string
  ratio: string
  description: string
}

export default function CuppingReport({ cupping, user_sample }: Props) {
  const totalScore = user_sample.total_score || 0
  const grade = getScoreGrade(totalScore)
  const scoreColor = getScoreColor(totalScore)
  const [copied, setCopied] = useState(false)
  const isCva = isCvaProtocol(cupping.cupping_form)

  // AI features state
  const [tastingNotes, setTastingNotes] = useState<TastingNotes | null>(null)
  const [loadingNotes, setLoadingNotes] = useState(false)
  const [copiedNotes, setCopiedNotes] = useState(false)
  const [brewRecipes, setBrewRecipes] = useState<BrewRecipe[] | null>(null)
  const [loadingRecipes, setLoadingRecipes] = useState(false)

  const handleGenerateTastingNotes = async () => {
    setLoadingNotes(true)
    try {
      const res = await fetch("/api/report_insights/tasting_notes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({ user_sample_id: user_sample.id }),
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setTastingNotes(data)
    } catch (e) {
      console.error("Failed to generate tasting notes:", e)
    } finally {
      setLoadingNotes(false)
    }
  }

  const handleGenerateBrewRecipes = async () => {
    setLoadingRecipes(true)
    try {
      const res = await fetch("/api/report_insights/brew_recipes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({ user_sample_id: user_sample.id }),
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setBrewRecipes(data.recipes || [])
    } catch (e) {
      console.error("Failed to generate brew recipes:", e)
    } finally {
      setLoadingRecipes(false)
    }
  }

  const handleShareLink = () => {
    const url = `${window.location.origin}/cuppings/${cupping.id}/public_report/${user_sample.id}`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleDownloadPDF = () => {
    import("jspdf").then(({ jsPDF }) => {
      const doc = new jsPDF()
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 20
      const green: [number, number, number] = [19, 160, 107] // #13A06B
      const slate: [number, number, number] = [61, 79, 92] // #3D4F5C
      const lightGray: [number, number, number] = [245, 245, 245]
      const borderGray: [number, number, number] = [220, 220, 220]
      let y = 0

      // ── Header bar ──────────────────────────────────────────────
      doc.setFillColor(...slate)
      doc.rect(0, 0, pageW, 38, "F")

      // Logo image - fetch as blob and convert to base64 for jsPDF
      const drawContent = (logoBase64?: string) => {
        if (logoBase64) {
          try {
            doc.addImage(logoBase64, "PNG", margin, 5, 28, 28)
          } catch (_) {}
        }

        // App name
        doc.setFont("helvetica", "bold")
        doc.setFontSize(16)
        doc.setTextColor(255, 255, 255)
        doc.text("Catador Pro", margin + 32, 17)

        // Tagline
        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(180, 220, 200)
        doc.text("Plataforma de Análisis Sensorial", margin + 32, 24)

        // Report label top-right
        doc.setFont("helvetica", "bold")
        doc.setFontSize(9)
        doc.setTextColor(180, 220, 200)
        doc.text("REPORTE DE CATACIÓN", pageW - margin, 22, { align: "right" })

        y = 50

        // ── Sample title ──────────────────────────────────────────
        doc.setFont("helvetica", "bold")
        doc.setFontSize(17)
        doc.setTextColor(...slate)
        doc.text(`${user_sample.sample.name}`, margin, y)
        y += 7

        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        doc.setTextColor(120, 120, 120)
        const protocolName =
          protocolLabels[cupping.cupping_form || ""] ||
          cupping.cupping_form ||
          ""
        doc.text(
          `Sesión TS-${cupping.id}  ·  ${protocolName}  ·  ${new Date(cupping.created_at).toLocaleDateString("es")}`,
          margin,
          y,
        )
        y += 10

        // ── Green divider ─────────────────────────────────────────
        doc.setDrawColor(...green)
        doc.setLineWidth(1.2)
        doc.line(margin, y, pageW - margin, y)
        y += 10

        // ── Score badge ───────────────────────────────────────────
        doc.setFillColor(...green)
        doc.roundedRect(margin, y, 60, 22, 4, 4, "F")
        doc.setFont("helvetica", "bold")
        doc.setFontSize(20)
        doc.setTextColor(255, 255, 255)
        doc.text(totalScore.toFixed(2), margin + 30, y + 14, {
          align: "center",
        })

        doc.setFont("helvetica", "normal")
        doc.setFontSize(9)
        doc.setTextColor(255, 255, 255)
        doc.text("Puntuación Total", margin + 30, y + 20, { align: "center" })
        y += 32

        // ── Sample details ─────────────────────────────────────────
        const details: [string, string][] = []
        if (user_sample.sample.origin)
          details.push(["País", user_sample.sample.origin])
        if (user_sample.sample.land)
          details.push(["Ubicación", user_sample.sample.land])
        if (user_sample.sample.producer)
          details.push(["Productor", user_sample.sample.producer])
        if (user_sample.sample.variety)
          details.push(["Varietal", user_sample.sample.variety])
        if (user_sample.sample.process)
          details.push(["Proceso", user_sample.sample.process])
        if (user_sample.roast_level)
          details.push(["Tueste", user_sample.roast_level])

        if (details.length > 0) {
          doc.setFillColor(...lightGray)
          doc.roundedRect(margin, y, pageW - margin * 2, 8, 2, 2, "F")
          doc.setFont("helvetica", "bold")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text("Detalles de la Muestra", margin + 4, y + 5.5)
          y += 12

          const colW = (pageW - margin * 2) / 2
          details.forEach(([label, value], i) => {
            const col = i % 2
            const row = Math.floor(i / 2)
            const xPos = margin + col * colW
            const yPos = y + row * 7
            doc.setFont("helvetica", "normal")
            doc.setFontSize(9)
            doc.setTextColor(120, 120, 120)
            doc.text(label + ":", xPos, yPos)
            doc.setFont("helvetica", "normal")
            doc.setTextColor(...slate)
            doc.text(value, xPos + 30, yPos)
          })
          y += Math.ceil(details.length / 2) * 7 + 6
        }

        // ── Scores table ──────────────────────────────────────────
        doc.setFillColor(...lightGray)
        doc.roundedRect(margin, y, pageW - margin * 2, 8, 2, 2, "F")
        doc.setFont("helvetica", "bold")
        doc.setFontSize(10)
        doc.setTextColor(...slate)
        doc.text("Puntuaciones", margin + 4, y + 5.5)
        y += 12

        const scores: [string, number | string][] = isCva
          ? [
              ["Fragancia", user_sample.fragrance_score],
              ["Aroma", user_sample.aroma_rating ?? 0],
              ["Sabor", user_sample.flavor_score],
              ["Retrogusto", user_sample.aftertaste_score],
              ["Acidez", user_sample.acidity_score],
              ["Dulzura", user_sample.sweetness_score],
              ["Cuerpo / Sensación en boca", user_sample.body_score],
              ["General", user_sample.overall_score],
              ["Tazas no uniformes", user_sample.non_uniform_cups?.length || 0],
              [
                "Tazas defectuosas",
                user_sample.defective_cups_list?.length || 0,
              ],
            ]
          : [
              ["Fragancia / Aroma", user_sample.fragrance_score],
              ["Acidez", user_sample.acidity_score],
              ["Cuerpo", user_sample.body_score],
              ["Sabor", user_sample.flavor_score],
              ["Retrogusto", user_sample.aftertaste_score],
              ["Balance", user_sample.balance_score],
              ["General", user_sample.overall_score],
              ["Uniformidad", user_sample.uniformity_score],
              ["Taza Limpia", user_sample.clean_cup_score],
              ["Dulzura", user_sample.sweetness_score],
              ["Defectos (tazas)", user_sample.defects_cups || 0],
            ]

        const rowH = 9
        scores.forEach(([label, value], i) => {
          const bg =
            i % 2 === 0
              ? lightGray
              : ([255, 255, 255] as [number, number, number])
          doc.setFillColor(...bg)
          doc.rect(margin, y, pageW - margin * 2, rowH, "F")

          doc.setDrawColor(...borderGray)
          doc.setLineWidth(0.2)
          doc.rect(margin, y, pageW - margin * 2, rowH)

          doc.setFont("helvetica", "normal")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text(String(label), margin + 4, y + 6.2)

          doc.setFont("helvetica", "bold")
          doc.setTextColor(...green)
          doc.text(String(value), pageW - margin - 4, y + 6.2, {
            align: "right",
          })

          y += rowH
        })

        y += 10

        // ── Notes ─────────────────────────────────────────────────
        if (user_sample.notes) {
          doc.setFillColor(...lightGray)
          doc.setDrawColor(...borderGray)
          doc.setLineWidth(0.3)
          doc.roundedRect(margin, y, pageW - margin * 2, 8, 2, 2, "FD")
          doc.setFont("helvetica", "bold")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text("Notas del catador", margin + 4, y + 5.5)
          y += 12

          doc.setFont("helvetica", "normal")
          doc.setFontSize(9)
          doc.setTextColor(80, 80, 80)
          const lines = doc.splitTextToSize(
            user_sample.notes,
            pageW - margin * 2 - 4,
          )
          doc.text(lines, margin + 2, y)
          y += lines.length * 5 + 6
        }

        // ── Descriptors ───────────────────────────────────────────
        const descMap = user_sample.aroma_sca_selections
        if (
          descMap &&
          typeof descMap === "object" &&
          Object.keys(descMap).length > 0
        ) {
          const catLabels: Record<string, string> = {
            fragrance: "Fragancia/Aroma",
            acidity: "Acidez",
            body: "Cuerpo",
            flavor: "Sabor",
            aftertaste: "Retrogusto",
            balance: "Balance",
          }
          doc.setFillColor(...lightGray)
          doc.roundedRect(margin, y, pageW - margin * 2, 8, 2, 2, "F")
          doc.setFont("helvetica", "bold")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text("Descriptores", margin + 4, y + 5.5)
          y += 12

          Object.entries(descMap).forEach(([cat, descs]) => {
            if (!descs || descs.length === 0) return
            doc.setFont("helvetica", "bold")
            doc.setFontSize(9)
            doc.setTextColor(120, 120, 120)
            doc.text(catLabels[cat] || cat, margin, y)
            doc.setFont("helvetica", "normal")
            doc.setTextColor(...slate)
            doc.text(
              descs.map((d) => getDescriptorLabel(d)).join(", "),
              margin + 40,
              y,
            )
            y += 6
          })
          y += 4
        }

        // ── Footer ────────────────────────────────────────────────
        doc.setFillColor(...slate)
        doc.rect(0, pageH - 14, pageW, 14, "F")
        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(180, 220, 200)
        doc.text("Catador Pro · catas.sabersobrecafe.com", margin, pageH - 5)
        doc.text(
          `Generado el ${new Date().toLocaleDateString("es")}`,
          pageW - margin,
          pageH - 5,
          { align: "right" },
        )

        doc.save(`reporte-${user_sample.sample.name}-TS${cupping.id}.pdf`)
      }

      fetch("/icon.png")
        .then((res) => res.blob())
        .then(
          (blob) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result as string)
              reader.onerror = reject
              reader.readAsDataURL(blob)
            }),
        )
        .then((base64) => drawContent(base64))
        .catch(() => drawContent())
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Reporte de Catación - ${cupping.title}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              href={cuppingsPath()}
              className="text-primary hover:text-primary/80 flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Muestras
            </Link>
            <span className="text-muted-foreground">|</span>
            <button className="text-primary hover:text-primary/80 flex items-center gap-2 text-sm">
              Ir a Información de Muestra
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleShareLink}
            >
              <Link2 className="h-4 w-4" />
              {copied ? "Enlace copiado!" : "Compartir enlace"}
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 gap-2"
              onClick={handleDownloadPDF}
            >
              <Download className="h-4 w-4" />
              Descargar reporte
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() =>
                router.post(coffeesPath(), {
                  coffee: {
                    name: user_sample.sample.name || cupping.title,
                    pinned_cupping_id: cupping.id,
                    pinned_user_sample_id: user_sample.id,
                  },
                })
              }
            >
              <Sparkles className="h-4 w-4" />
              Crear Pagina de Producto
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content Area */}
          <div className="space-y-6 lg:col-span-2">
            {/* Tasting Info Card */}
            <Card>
              <CardContent className="p-6">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">
                      {user_sample.sample.name} (TS-{cupping.id})
                    </h2>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">
                        Protocolo:
                      </span>
                      <span className="text-primary text-sm font-semibold">
                        {protocolLabels[cupping.cupping_form || ""] ||
                          cupping.cupping_form ||
                          "N/A"}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        · Catación Regular
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-muted-foreground text-sm">
                      Puntuación Promedio
                    </div>
                    <div className="text-primary text-2xl font-bold">
                      {totalScore.toFixed(1)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sample Details Card */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 text-lg font-semibold">
                  Detalles de la Muestra
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    {user_sample.sample.origin && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">País</span>
                        <span className="font-medium">
                          {user_sample.sample.origin}
                        </span>
                      </div>
                    )}
                    {user_sample.sample.land && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">Ubicación</span>
                        <span className="font-medium">
                          {user_sample.sample.land}
                        </span>
                      </div>
                    )}
                    {user_sample.sample.producer && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">Productor</span>
                        <span className="font-medium">
                          {user_sample.sample.producer}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {user_sample.sample.variety && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">Varietal</span>
                        <span className="font-medium">
                          {user_sample.sample.variety}
                        </span>
                      </div>
                    )}
                    {user_sample.sample.process && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">Proceso</span>
                        <span className="font-medium capitalize">
                          {user_sample.sample.process}
                        </span>
                      </div>
                    )}
                    {user_sample.roast_level && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-muted-foreground">
                          Nivel de tueste
                        </span>
                        <span className="font-medium capitalize">
                          {user_sample.roast_level}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sample Type Information */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 text-lg font-bold">
                  Información del tipo de muestra
                </h3>

                <div className="space-y-4">
                  {/* Roast Level */}
                  <div className="flex items-center justify-between border-b pb-3">
                    <span className="text-muted-foreground">
                      Nivel de tueste
                    </span>
                    <div
                      className="h-8 w-8 rounded-full border-2 border-gray-300"
                      style={{
                        backgroundColor: getRoastLevelColor(
                          user_sample.roast_level || "medium",
                        ),
                      }}
                    />
                  </div>

                  {/* Number of Cups */}
                  <div className="flex items-center justify-between border-b pb-3">
                    <span className="text-muted-foreground">
                      Número de Tazas
                    </span>
                    <span className="font-medium">
                      {user_sample.total_cups}
                    </span>
                  </div>

                  {isCva ? (
                    <>
                      {/* CVA Affective scores */}
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Fragancia</span>
                        <span className="text-primary font-medium">
                          {user_sample.fragrance_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Aroma</span>
                        <span className="text-primary font-medium">
                          {user_sample.aroma_rating}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Sabor</span>
                        <span className="text-primary font-medium">
                          {user_sample.flavor_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Retrogusto
                        </span>
                        <span className="text-primary font-medium">
                          {user_sample.aftertaste_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Acidez</span>
                        <span className="text-primary font-medium">
                          {user_sample.acidity_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Dulzura</span>
                        <span className="text-primary font-medium">
                          {user_sample.sweetness_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Cuerpo / Sensación en boca
                        </span>
                        <span className="text-primary font-medium">
                          {user_sample.body_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">General</span>
                        <span className="text-primary font-medium">
                          {user_sample.overall_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Tazas no uniformes
                        </span>
                        <span className="font-medium">
                          {user_sample.non_uniform_cups?.length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Tazas defectuosas
                        </span>
                        <span className="font-medium">
                          {user_sample.defective_cups_list?.length || 0}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* SCA scores */}
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Fragancia / Aroma
                        </span>
                        <span className="text-primary font-medium">
                          {user_sample.fragrance_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Acidez</span>
                        <span className="text-primary font-medium">
                          {user_sample.acidity_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Cuerpo</span>
                        <span className="text-primary font-medium">
                          {user_sample.body_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Sabor</span>
                        <span className="text-primary font-medium">
                          {user_sample.flavor_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Retrogusto
                        </span>
                        <span className="text-primary font-medium">
                          {user_sample.aftertaste_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Balance</span>
                        <span className="text-primary font-medium">
                          {user_sample.balance_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">
                          Taza limpia
                        </span>
                        <span className="text-primary font-medium">
                          {user_sample.clean_cup_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Dulzura</span>
                        <span className="text-primary font-medium">
                          {user_sample.sweetness_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">General</span>
                        <span className="text-primary font-medium">
                          {user_sample.overall_score}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b pb-3">
                        <span className="text-muted-foreground">Defectos</span>
                        <span className="font-medium">
                          {user_sample.defects_cups || 0}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Notes */}
                  <div className="border-b pb-3">
                    <div className="text-muted-foreground mb-2">Notas</div>
                    {user_sample.notes ? (
                      <p className="text-sm">{user_sample.notes}</p>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Tasting Notes */}
            <Card>
              <CardContent className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-semibold">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    Notas de Cata AI
                  </h3>
                  {tastingNotes && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          tastingNotes.consumer +
                            "\n\n" +
                            tastingNotes.technical,
                        )
                        setCopiedNotes(true)
                        setTimeout(() => setCopiedNotes(false), 2000)
                      }}
                    >
                      {copiedNotes ? (
                        <>
                          <Check className="h-3 w-3" /> Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" /> Copiar
                        </>
                      )}
                    </Button>
                  )}
                </div>
                {!tastingNotes && !loadingNotes && (
                  <div className="py-4 text-center">
                    <p className="text-muted-foreground mb-3 text-sm">
                      Genera notas de cata para consumidores a partir de tu
                      evaluacion
                    </p>
                    <Button
                      onClick={handleGenerateTastingNotes}
                      className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    >
                      <Sparkles className="h-4 w-4" />
                      Generar Notas de Cata
                    </Button>
                  </div>
                )}
                {loadingNotes && (
                  <div className="flex items-center justify-center gap-2 py-8">
                    <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                    <span className="text-muted-foreground text-sm">
                      Generando notas...
                    </span>
                  </div>
                )}
                {tastingNotes && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                        Para consumidor
                      </p>
                      <p className="text-sm leading-relaxed">
                        {tastingNotes.consumer}
                      </p>
                    </div>
                    {tastingNotes.technical && (
                      <div>
                        <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                          Tecnica
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {tastingNotes.technical}
                        </p>
                      </div>
                    )}
                    {tastingNotes.flavor_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {tastingNotes.flavor_tags.map((tag: string) => (
                          <span
                            key={tag}
                            className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Brew Recipes */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 flex items-center gap-2 font-semibold">
                  <Coffee className="h-4 w-4 text-amber-700" />
                  Recetas Sugeridas AI
                </h3>
                {!brewRecipes && !loadingRecipes && (
                  <div className="py-4 text-center">
                    <p className="text-muted-foreground mb-3 text-sm">
                      Obtén recetas de preparacion optimizadas para este perfil
                      sensorial
                    </p>
                    <Button
                      onClick={handleGenerateBrewRecipes}
                      variant="outline"
                      className="gap-2"
                    >
                      <Coffee className="h-4 w-4" />
                      Sugerir Recetas
                    </Button>
                  </div>
                )}
                {loadingRecipes && (
                  <div className="flex items-center justify-center gap-2 py-8">
                    <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                    <span className="text-muted-foreground text-sm">
                      Generando recetas...
                    </span>
                  </div>
                )}
                {brewRecipes && (
                  <div className="grid gap-3 md:grid-cols-3">
                    {brewRecipes.map((recipe: BrewRecipe) => (
                      <div
                        key={recipe.method}
                        className="rounded-lg border p-4"
                      >
                        <h4 className="mb-2 font-semibold">{recipe.method}</h4>
                        <div className="text-muted-foreground space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Dosis</span>
                            <span className="text-foreground font-medium">
                              {recipe.dose_g}g
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Agua</span>
                            <span className="text-foreground font-medium">
                              {recipe.water_ml}ml
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Temp</span>
                            <span className="text-foreground font-medium">
                              {recipe.temp_c}°C
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Molienda</span>
                            <span className="text-foreground font-medium">
                              {recipe.grind}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Tiempo</span>
                            <span className="text-foreground font-medium">
                              {recipe.time}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Ratio</span>
                            <span className="text-foreground font-medium">
                              {recipe.ratio}
                            </span>
                          </div>
                        </div>
                        <p className="text-muted-foreground mt-2 border-t pt-2 text-xs">
                          {recipe.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar - Session Card + Charts */}
          <div className="space-y-6 lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <div className="mb-4">
                  <div className="text-muted-foreground mb-2 text-xs">
                    ID: TS-{cupping.id}
                  </div>
                  <div className="mb-1 text-sm font-medium">
                    {cupping.title}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {user_sample.sample.name}
                  </div>
                </div>

                <div className="mb-6 flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    Puntuación Prom.
                  </span>
                  <span className="text-3xl font-bold">
                    {totalScore.toFixed(1)}
                  </span>
                </div>

                {/* Score Badge */}
                <div className="mb-6 flex items-center justify-center gap-2">
                  <div className="bg-primary flex items-center gap-2 rounded-full px-4 py-2">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${scoreColor} font-bold text-white`}
                    >
                      {grade}
                    </div>
                    <span className="font-medium text-white">
                      {user_sample.user_name || "Catador"}
                    </span>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                      <span className="text-primary text-lg font-bold">
                        {totalScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Participant Info */}
                {cupping.participant_count !== undefined && (
                  <div className="text-muted-foreground text-center text-sm">
                    {cupping.participant_count}{" "}
                    {cupping.participant_count === 1
                      ? "participante"
                      : "participantes"}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Radar Chart */}
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 text-center text-sm font-semibold">
                  Perfil Sensorial
                </h3>
                <div className="h-[280px] w-full">
                  <ScoreRadarChart
                    protocol={isCva ? "cva" : "sca"}
                    scores={user_sample}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Descriptors */}
            {user_sample.aroma_sca_selections &&
              Object.keys(user_sample.aroma_sca_selections).length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="mb-4 text-sm font-semibold">Descriptores</h3>
                    <div className="space-y-3">
                      {Object.entries(user_sample.aroma_sca_selections).map(
                        ([category, descriptors]) => {
                          if (!descriptors || descriptors.length === 0)
                            return null
                          return (
                            <div key={category}>
                              <p className="text-muted-foreground mb-1 text-xs font-medium capitalize">
                                {category === "fragrance"
                                  ? "Fragancia / Aroma"
                                  : category === "acidity"
                                    ? "Acidez"
                                    : category === "body"
                                      ? "Cuerpo"
                                      : category === "flavor"
                                        ? "Sabor"
                                        : category === "aftertaste"
                                          ? "Retrogusto"
                                          : category === "balance"
                                            ? "Balance"
                                            : category}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {descriptors.map((d) => (
                                  <span
                                    key={d}
                                    className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium"
                                  >
                                    {getDescriptorLabel(d)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )
                        },
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
          </div>
        </div>

        {/* Organization Branding Footer */}
        {cupping.organization && (
          <div className="mt-8 border-t py-6 text-center">
            <p className="text-muted-foreground text-sm">
              Evaluado por{" "}
              <span className="text-primary font-medium">
                {cupping.organization.name}
              </span>
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
